// Third-party packages
// Third-party packages
import {
	EnumButtonCorners,
	EnumButtonSize,
} from '@enums/button.enum';
import {
	EnumThemeLink,
} from '@enums/link.enum';
import {
	EnumSpecificationSteps,
} from '@enums/steps.enum';
import {
	EnumTheme,
} from '@enums/theme.enum';
import {
	getURL,
} from '@modules/utils';
import {
	SPECIFICATION_MANAGE_ROUTES,
} from '@routes/lpdipro/private/specification';
import {
	PATHS,
} from '@routes/paths';
import {
	RefObject,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	useTranslation,
} from 'react-i18next';
import {
	useSelector,
} from 'react-redux';
import {
	matchRoutes,
	useLocation,
} from 'react-router-dom';

// Project modules
import {
	selectIsNavOpen,
} from '@stores/selectors/appSelectors';

// Templates / Layouts / Pages
import {
	SpecificationJson,
} from '@layouts/Pages/PageSpecifications';

// UI Components
import {
	Button,
} from '@components/button';
import {
	Link,
} from '@components/link';
import {
	Sticky,
} from '@components/sticky';
import {
	TabList,
} from '@components/tab-list';
import {
	Tab,
} from '@components/tab-list/tab';

// Style imports
import styles from './header.module.scss';

interface HeaderProps {
	specification: SpecificationJson;
	isDesktopResolution: boolean;
	contentSpecificationRef: RefObject<HTMLDivElement>;
	step: EnumSpecificationSteps;
}

export const Header = ({
	contentSpecificationRef,
	isDesktopResolution,
	specification,
	step,
}: HeaderProps) => {
	const {
		t,
	} = useTranslation();
	const {
		pathname,
	} = useLocation();
	const isNavOpen = useSelector(selectIsNavOpen);

	// Check if the actual route contains one of the 'manage' section
	const isManageRoute = matchRoutes(SPECIFICATION_MANAGE_ROUTES, pathname);

	const titleElement = (
		<div className={styles.title}>
			<h1>
				{t('format.capitalize', {
					text: specification.name,
				})}
			</h1>
		</div>
	);

	const reportURL = getURL(PATHS.SPECIFICATIONS.REPORT, {
		':specificationid': `${specification.id}`,
	});

	const manageURL = getURL(PATHS.SPECIFICATIONS.MANAGE.STEP._ROOT, {
		':specificationid': `${specification.id}`,
		':step': `${step}`,
	});

	const linkListElement = (
		<TabList className={styles.link_list}>
			<Tab
				disabled={true}
				isActive={/report/.test(location.pathname)}
			>
				<Link
					href={reportURL}
					theme={EnumThemeLink.TAB}
				>
					{t('format.capitalize', {
						text: t('page.specification.report.name'),
					})}
				</Link>
			</Tab>
			<Tab isActive={/manage/.test(location.pathname)}>
				<Link
					href={manageURL}
					theme={EnumThemeLink.TAB}
				>
					{t('format.capitalize', {
						text: t('page.specification.manage.name'),
					})}
				</Link>
			</Tab>
		</TabList>
	);

	const [
		headerCssClasses,
		setHeaderCssClasses,
	] = useState([
		styles.header,
	]);

	const [
		isSticky,
		setIsSticky,
	] = useState(false);

	const [
		stickyCssClasses,
		setStickyCssClasses,
	] = useState([
		styles.filter__sticky,
	]);

	useEffect(() => {
		const baseFilterClasses = [
			styles.filter__sticky,
		];
		const baseHeaderClasses = [
			styles.header,
		];
		if (isNavOpen && isDesktopResolution) {
			baseHeaderClasses.push(styles.header__navisopen);
		}
		if (isSticky && isNavOpen && isDesktopResolution) {
			baseFilterClasses.push(styles.filter__sticky__navisopen);
		}
		setHeaderCssClasses(baseHeaderClasses);
		setStickyCssClasses(baseFilterClasses);
	}, [
		isNavOpen,
		isSticky,
	]);

	const headerStickyElement = (
		<>
			<Button
				className={styles.button}
				corners={EnumButtonCorners.Default}
				counter={specification?.counters ? specification.counters[EnumSpecificationSteps.MATCHED] : null}
				isActive={step === EnumSpecificationSteps.MATCHED}
				label={t('format.capitalize', {
					text: t('page.specification.manage.steps.matched.title'),
				})}
				size={EnumButtonSize.SMALL}
				theme={EnumTheme.SECONDARY}
				to={getURL(PATHS.SPECIFICATIONS.MANAGE.STEP._ROOT, {
					':specificationid': `${specification.id}`,
					':step': EnumSpecificationSteps.MATCHED,
				})}
			/>
			<Button
				className={styles.button}
				corners={EnumButtonCorners.Default}
				counter={specification?.counters ? specification?.counters[EnumSpecificationSteps.UPDATED] : null}
				isActive={step === EnumSpecificationSteps.UPDATED}
				label={t('format.capitalize', {
					text: t('page.specification.manage.steps.updated.title'),
				})}
				size={EnumButtonSize.SMALL}
				theme={EnumTheme.SECONDARY}
				to={getURL(PATHS.SPECIFICATIONS.MANAGE.STEP._ROOT, {
					':specificationid': `${specification.id}`,
					':step': EnumSpecificationSteps.UPDATED,
				})}
			/>
			<Button
				className={styles.button}
				corners={EnumButtonCorners.Default}
				counter={specification?.counters ? specification?.counters[EnumSpecificationSteps.IGNORED] : null}
				isActive={step === EnumSpecificationSteps.IGNORED}
				label={t('format.capitalize', {
					text: t('page.specification.manage.steps.ignored.title'),
				})}
				size={EnumButtonSize.SMALL}
				theme={EnumTheme.SECONDARY}
				to={getURL(PATHS.SPECIFICATIONS.MANAGE.STEP._ROOT, {
					':specificationid': `${specification.id}`,
					':step': EnumSpecificationSteps.IGNORED,
				})}
			/>
		</>
	);

	const stickyContainerRef = useRef(null);

	return (
		<div className={headerCssClasses.join(' ')}>
			{titleElement}
			{linkListElement}
			{isManageRoute ? (
				<>
					<div
						className={styles.filter}
						ref={stickyContainerRef}
					>
						{headerStickyElement}
					</div>
					<Sticky
						className={stickyCssClasses.join(' ')}
						isDesktopResolution={true}
						isSticky={isSticky}
						scrollContainerRef={isDesktopResolution ? contentSpecificationRef : undefined}
						setIsSticky={setIsSticky}
						stickyContainerRef={stickyContainerRef}
					>
						{headerStickyElement}
					</Sticky>
				</>
			) : null}
		</div>
	);
};
